import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FileUp, Loader2, Trash2 } from "lucide-react";
import { uploadAdminFile } from "../../redux/slices/fileSlice";
import { resolveAssetUrl, isImagePath } from "../../lib/asset-url";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { useToast } from "../ui/use-toast";

/**
 * Upload a file (image, PDF, video) or optionally paste an external link (e.g. YouTube).
 */
function FileOrLinkField({
  label = "File or link",
  hint,
  value = "",
  onChange,
  category = "content_media",
  disabled = false,
  allowExternalLink = true,
  externalLinkLabel = "Or paste a link (YouTube, etc.)",
  accept = "image/*,video/mp4,video/webm,application/pdf",
}) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewUrl = value && isImagePath(value) ? resolveAssetUrl(value) : "";
  const isExternal =
    value &&
    (value.startsWith("http://") || value.startsWith("https://")) &&
    !value.includes("/uploads/");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled) return;

    setUploading(true);
    try {
      const result = await dispatch(
        uploadAdminFile({ file, category })
      ).unwrap();
      const url = result?.file_url;
      if (!url) throw new Error("Upload did not return a file URL");
      onChange(url);
      toast({ title: "File uploaded" });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: typeof err === "string" ? err : "Could not upload file",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid gap-3">
      <div>
        <Label>{label}</Label>
        {hint ? (
          <p className="text-xs text-muted-foreground mt-1">{hint}</p>
        ) : null}
      </div>

      {previewUrl ? (
        <div className="relative max-w-xs rounded-lg border overflow-hidden">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-32 object-cover"
          />
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute top-2 right-2 h-8 w-8"
            disabled={disabled}
            onClick={() => onChange("")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ) : value && !isExternal ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground max-w-md">
          <span className="truncate flex-1">{value}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange("")}
          >
            Remove
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={onPick}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <FileUp className="h-4 w-4 mr-2" />
          )}
          Upload file
        </Button>
      </div>

      {allowExternalLink && (
        <div className="grid gap-1">
          <Label className="text-xs font-normal text-muted-foreground">
            {externalLinkLabel}
          </Label>
          <Input
            value={isExternal ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://youtube.com/watch?v=…"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

export default FileOrLinkField;
